import React, { useEffect } from "react";
import QzTrayPreviewLogo from "./QzTrayPreviewLogo";
import QzTrayPreviewText from "./QzTrayPreviewText";
import QzTrayPreviewQr from "./QzTrayPreviewQr";
import { Loader2 } from "lucide-react";
import { useQzTrayPreview } from "../../../hooks/qztray/useQzTrayPreview";

/**
 * 👁️ QzTrayPreviewPanel
 *
 * Panel visual para previsualizar tickets (QZ Tray Preview)
 * Incluye:
 * - Logo en Base64
 * - Contenido textual del ticket
 * - Código QR
 * - Metadatos (referencia y fecha)
 *
 * ✅ Soporta modo claro y oscuro.
 */
const QzTrayPreviewPanel: React.FC = () => {
  const {
    preview,
    isLoading,
    error,
    fetchSamplePreview,
    clearPreview,
  } = useQzTrayPreview();

  useEffect(() => {
    fetchSamplePreview();
  }, []);

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark p-6 mt-6">
      {/* === Header === */}
      <div className="flex items-center justify-between border-b border-stroke pb-4 dark:border-strokedark">
        <h3 className="font-medium text-black dark:text-white">
          Vista previa del ticket
        </h3>

        <div className="flex gap-2">
          <button
            onClick={fetchSamplePreview}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-2 rounded bg-primary py-1.5 px-4 text-sm font-medium text-white hover:bg-opacity-90 disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin w-4 h-4" /> Generando...
              </>
            ) : (
              "Generar muestra"
            )}
          </button>

          <button
            onClick={clearPreview}
            className="inline-flex items-center justify-center rounded bg-[#DC3545] py-1.5 px-4 text-sm font-medium text-white hover:bg-opacity-90"
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* === Estado de carga === */}
      {isLoading && (
        <div className="text-center text-gray-500 dark:text-gray-400 py-6 italic">
          Generando vista previa...
        </div>
      )}

      {/* === Error === */}
      {error && (
        <div className="text-center text-red-600 dark:text-red-400 py-4">
          {error}
        </div>
      )}

      {/* === Contenido del ticket === */}
      {!isLoading && preview && (
        <div className="flex flex-col items-center pt-6">
          <QzTrayPreviewLogo base64={preview.payload.logo} />
          <QzTrayPreviewText base64={preview.payload.ticket} />
          <QzTrayPreviewQr base64={preview.payload.qr} />

          {/* Metadatos */}
          <div className="mt-6 text-sm text-gray-600 dark:text-gray-400 text-center space-y-1">
            <p>
              <strong>Referencia:</strong> {preview.referenceId}
            </p>
            <p>
              <strong>Generado:</strong> {preview.generatedAt}
            </p>
            <p className="italic">{preview.description}</p>
          </div>
        </div>
      )}

      {/* === Sin datos === */}
      {!isLoading && !preview && !error && (
        <div className="text-center text-gray-400 dark:text-gray-500 py-6 italic">
          No hay vista previa disponible. Haz clic en “Generar muestra”.
        </div>
      )}
    </div>
  );
};

export default QzTrayPreviewPanel;
